import { Injectable } from '@angular/core';
import { fromEvent, merge, debounceTime } from 'rxjs';
import { AuthService } from './auth.service';



/**
 * @author Brayan Cataño
 * @abstract Listen to user DOM activities constantly, in order to determine if the user is AFK.
 * It checks every {@link TIMEOUT_IN_MS} miliseconds.
 * @summary Checks for AFK user and logs him out if so.
 */
@Injectable({
  providedIn: 'root',
})
export class ActivityListernerService {
  private inactivityTimerClock!: ReturnType<typeof setTimeout>;
  private readonly TIMEOUT_IN_MS = 30 * 60000; // 30 minutes multiplied for 60000 to convert minutes to ms.

  constructor(private authService: AuthService) {

    this.startMonitoring();
  }

  /**
   *  Listen for user activity (mouse move, keypress, scroll)
   */
  startMonitoring() {

    /**
     * combines all DOM events into one observable 
     * @see https://rxjs.dev/api/index/function/merge
     * @see https://rxjs.dev/api/index/function/fromEvent
     */
    const events = merge(
      fromEvent(document, 'mousemove'),
      fromEvent(document, 'keydown'),
      fromEvent(document, 'scroll')
    );

    // Restart the timer on any activity with a debounceTime to save on performance.
    const DEBOUNCE_MILISECONDS = 2000; // 2s
    events.pipe(debounceTime(DEBOUNCE_MILISECONDS)).subscribe(() => this.resetInactivityTimer());

    // Start inactivity timer
    this.resetInactivityTimer();
  }

  // Reset inactivity timer
  private resetInactivityTimer() {
    if (this.inactivityTimerClock) {
      clearTimeout(this.inactivityTimerClock);
    }

    /**
     * if the User is currently logged in, the signal comming from the authService will return true, initializing the countdown.
     * @see https://angular.dev/guide/signals
     */
    if(this.authService.userIsLoggedInSignal()){
      this.inactivityTimerClock = setTimeout(() => this.authService.logout(), this.TIMEOUT_IN_MS);
    }
  }

}
